import { useQuery } from "@tanstack/react-query";
import { userKeys } from "@/queries/keys";
import { TRANSCRIPTION_LANGUAGE_OPTIONS } from "@/lib/utils/language";
import {
  getProviderKeys,
  getPreferences,
  getCustomization,
  checkLinkAccounts,
  getTranscriptionLanguages,
  getAssistantTemplates,
} from "./api";
import type { CompanyPreferences, MetaData, AssistantTemplate } from "./api";

export type { AssistantTemplate };

export function usePreferences() {
  return useQuery({
    queryKey: userKeys.preferences(),
    queryFn: async () => {
      const res = await getPreferences();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch preferences");
      return res.data as CompanyPreferences;
    },
  });
}

export function useCustomization() {
  return useQuery({
    queryKey: userKeys.customization(),
    queryFn: async () => {
      const res = await getCustomization();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch customization");
      return res.data as MetaData;
    },
  });
}

export function useLinkedAccounts() {
  return useQuery({
    queryKey: userKeys.linkedAccounts(),
    queryFn: async () => {
      const res = await checkLinkAccounts();
      if (!res.success)
        throw new Error(res.error ?? "Failed to check linked accounts");
      return res.data!;
    },
  });
}

export function useAssistantTemplates() {
  return useQuery({
    queryKey: ["assistant-templates"] as const,
    queryFn: async () => {
      const res = await getAssistantTemplates();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch templates");
      const data = res.data;
      return Array.isArray(data) ? data : (data?.assistants ?? []);
    },
  });
}

// value is renamed to code here (the single mapping point); don't add a second mapping site downstream.
export interface TranscriptionLanguage {
  code: string;
  name: string;
  isVocab: boolean;
  isLive: boolean;
}

const STATIC_FALLBACK: TranscriptionLanguage[] = TRANSCRIPTION_LANGUAGE_OPTIONS;

export function useTranscriptionLanguages() {
  return useQuery<TranscriptionLanguage[]>({
    queryKey: userKeys.languages(),
    queryFn: async () => {
      try {
        const res = await getTranscriptionLanguages();
        if (res.success && res.data?.length) {
          const live = res.data
            .filter((l) => l.value && l.name)
            .map((l) => ({
              code: l.value,
              name: l.name,
              isVocab: !!l.isVocab,
              isLive: !!l.isLive,
            }));
          if (live.length > 0) return live;
        }
      } catch {
      }
      return STATIC_FALLBACK;
    },
    placeholderData: STATIC_FALLBACK,
    staleTime: 1000 * 60 * 60,
  });
}

// 404 is the gate (not an error) and resolves to null; retry:false stops gated workspaces retrying.
export function useProviderKeys() {
  return useQuery({
    queryKey: userKeys.providerKeys(),
    retry: false,
    queryFn: async () => {
      const res = await getProviderKeys();
      if (!res.success) {
        if (res.status === 404) return null;
        throw new Error(res.error ?? "Failed to load provider keys");
      }
      return res.data ?? [];
    },
  });
}
